import { theme } from '@styles/theme';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import { useSearchDeals } from '../../../../hooks/header/UseSearchDeals';
import { useCheckDevice } from '../../../../hooks/UseCheckDevice';
import { AppState } from '../../../../store/AppState';
import { DealerState } from '../../../../types/DealerState';
import { SearchResult } from '../../../../types/header/SearchResult';
import { Icon } from '../../../style/Icon';
import {
  BPS,
  DealInfo,
  FootNote,
  Meta,
  RecallSticker,
  SearchResultsSection,
  StyledSpan,
  YearTrim,
} from './SearchResultsStyles';

type SearchResultsProps = {
  searchResults: Array<SearchResult>;
  goToDealsheet: (item: SearchResult) => void;
};

const SearchResults: FC<SearchResultsProps> = ({ searchResults, goToDealsheet }): JSX.Element => {
  const {
    getName,
    updateMakeName,
    isTrimAndYearValid,
    formatTimeGap,
    onPrivateIncentiveDealerCheck,
  } = useSearchDeals();
  const { storeServices, featureToggle } = useSelector<AppState, DealerState>(
    (state) => state.dealer,
  );
  const { isMobileDevice, isTabletDevice } = useCheckDevice();

  return (
    <div>
      <SearchResultsSection data-testid='search-results' isMobileDevice={isMobileDevice}>
        {searchResults.map((result: SearchResult) => {
          return (
            <li
              key={result.vin}
              className={`single-notification ui-menu-item ${result.isActive ? '' : 'result-inactive'}`}
              onMouseDown={() => goToDealsheet(result)}
              data-testid={result.vin}
            >
              <DealInfo>
                <p className='customer'>{getName(result)}</p>
                {isTrimAndYearValid(result) && (
                  <YearTrim>
                    {result.year + ' ' + updateMakeName(result.make) + ' ' + result.trim}
                  </YearTrim>
                )}
                {!result.isActive && (
                  <FootNote>
                    <Icon type='warning' size={15} additionalClassName='am-icon' />
                    Deactivated by <b>{result.deactivationInfo.name}</b>
                    {result.deactivationInfo.days != null && (
                      <span className={isTabletDevice ? '' : 'no-wrap'}>
                        {formatTimeGap(result.deactivationInfo.days)}
                      </span>
                    )}
                    .&nbsp;
                    {!isMobileDevice && (
                      <StyledSpan onClick={() => goToDealsheet(result)}>Reactivate</StyledSpan>
                    )}
                  </FootNote>
                )}
              </DealInfo>
              <Meta>
                {result.hasOpenRecall && storeServices.hasRecall && featureToggle.recall && (
                  <RecallSticker data-testid='recall-sticker'>
                    <em className='am-icon recall am-icon-recall'></em>
                  </RecallSticker>
                )}
                {onPrivateIncentiveDealerCheck(result) && (
                  <span
                    className={`private-incentive purple-icon  i am-icon-private-incentive ${result.hasOpenRecall ? 'with-recall' : 'without-recall'}`}
                  />
                )}
                {result.dealType === 1 && <span className='deal-type-sticker Lease'>L</span>}
                {result.dealType === 2 && <span className='deal-type-sticker Finance'>F</span>}
                {result.dealType === 4 && <span className='deal-type-sticker Cash'>C</span>}
                {featureToggle?.fordPrivateIncentive && result.isFmcPrivateIncentive && (
                  <Icon
                    type='incentives-alt'
                    size={20}
                    additionalClassName='ford-ici-sticker'
                    data-testid='ford-private-incentive-search'
                    color={theme.palette.purpleEight}
                  />
                )}
                <BPS isActive={result.isActive}>
                  <em>
                    BPS
                    <span>&reg;</span>
                  </em>
                  <b>{result.bps > -1 ? result.bps : 'N/A'}</b>
                </BPS>
              </Meta>
            </li>
          );
        })}
      </SearchResultsSection>
    </div>
  );
};

export default SearchResults;
